import * as React from "react";

import { useTranslation } from "react-i18next";

import { CheckIcon, Tag as TagIcon } from "lucide-react";

import { useItemLabels, useItems } from "@/api/items";
import type { Item, ItemLabel } from "@/api/items";
import { useEntitySelection } from "@/components/entity-select/useEntitySelection";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";

import { Pill } from "./Pill";

export type ForSelection = {
  items: number[];
  labels: number[];
};

export type ForPillProps = {
  value: ForSelection;
  onChange: (next: ForSelection) => void;
};

export function ForPill({ value, onChange }: ForPillProps) {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState("");

  const useLabelSearch = (q: string) => {
    const { data, isLoading } = useItemLabels();
    const all = data ?? [];
    const list = q
      ? all.filter((l) => l.name.toLowerCase().includes(q.toLowerCase()))
      : all;
    return { data: list, isLoading };
  };
  const useLabelResolve = (ids: number[]) => {
    const { data } = useItemLabels();
    return { data: data?.filter((l) => ids.includes(l.id)) };
  };
  const useItemSearch = (q: string) => {
    const r = useItems({ search: q || undefined, limit: 50 });
    return { data: r.data?.results ?? [], isLoading: r.isLoading };
  };
  const useItemResolve = (ids: number[]) => {
    const r = useItems({ ids, limit: ids.length });
    return { data: r.data?.results ?? [] };
  };

  const labelSel = useEntitySelection<ItemLabel>({
    value: value.labels,
    query,
    useSearchResults: useLabelSearch,
    useResolveSelected: useLabelResolve,
    getId: (l) => l.id,
  });
  const itemSel = useEntitySelection<Item>({
    value: value.items,
    query,
    useSearchResults: useItemSearch,
    useResolveSelected: useItemResolve,
    getId: (i) => i.id,
  });

  const totalSelected = value.items.length + value.labels.length;
  const isDefault = totalSelected === 0;
  const resolvedNames = [
    ...labelSel.selectedEntities.map((l) => l.name),
    ...itemSel.selectedEntities.map((i) => i.name),
  ];
  const label = isDefault
    ? t("demand.pills.for.allItems")
    : resolvedNames.length > 0
      ? summarizeSelection(resolvedNames)
      : t("demand.pills.for.selectedCount", { count: totalSelected });

  function toggleItem(id: number) {
    onChange({
      ...value,
      items: value.items.includes(id)
        ? value.items.filter((v) => v !== id)
        : [...value.items, id],
    });
  }

  function toggleLabel(id: number) {
    onChange({
      ...value,
      labels: value.labels.includes(id)
        ? value.labels.filter((v) => v !== id)
        : [...value.labels, id],
    });
  }

  const hasSelection =
    labelSel.selectedEntities.length > 0 || itemSel.selectedEntities.length > 0;
  const showTagsResults = labelSel.results.length > 0;
  const showItemsResults = itemSel.results.length > 0;

  return (
    <Pill
      label={label}
      isDefault={isDefault}
      onClear={
        isDefault ? undefined : () => onChange({ items: [], labels: [] })
      }
    >
      <Command shouldFilter={false}>
        <CommandInput
          placeholder={t("demand.pills.for.searchPlaceholder")}
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {(labelSel.isLoading || itemSel.isLoading) && (
            <div className="text-muted-foreground p-2 text-sm">
              {t("common.loading")}
            </div>
          )}
          <CommandEmpty>{t("demand.pills.noMatches")}</CommandEmpty>

          {hasSelection && (
            <>
              <CommandGroup
                heading={t("components.entitySelect.selectedHeading")}
              >
                {labelSel.selectedEntities.map((l) => (
                  <CommandItem
                    key={`sel-label-${l.id}`}
                    value={`sel-tag-${l.name}-${l.id}`}
                    onSelect={() => toggleLabel(l.id)}
                  >
                    <TagIcon className="mr-2 size-4 shrink-0" /> {l.name}
                    <CheckIcon className="ml-auto size-4 opacity-100" />
                  </CommandItem>
                ))}
                {itemSel.selectedEntities.map((it) => (
                  <CommandItem
                    key={`sel-item-${it.id}`}
                    value={`sel-item-${it.name}-${it.id}`}
                    onSelect={() => toggleItem(it.id)}
                  >
                    {it.name}
                    <CheckIcon className="ml-auto size-4 opacity-100" />
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {showTagsResults && (
            <CommandGroup heading={t("demand.pills.for.tagsGroup")}>
              {labelSel.results.map((l) => (
                <CommandItem
                  key={`label-${l.id}`}
                  value={`tag-${l.name}-${l.id}`}
                  onSelect={() => toggleLabel(l.id)}
                >
                  <TagIcon className="mr-2 size-4 shrink-0" /> {l.name}
                  <CheckIcon className="ml-auto size-4 opacity-0" />
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {showTagsResults && showItemsResults && <CommandSeparator />}

          {showItemsResults && (
            <CommandGroup heading={t("demand.pills.for.itemsGroup")}>
              {itemSel.results.map((it) => (
                <CommandItem
                  key={`item-${it.id}`}
                  value={`item-${it.name}-${it.id}`}
                  onSelect={() => toggleItem(it.id)}
                >
                  {it.name}
                  <CheckIcon className="ml-auto size-4 opacity-0" />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </Pill>
  );
}

/** "Widget A" | "Widget A + 2" | "" — resolved names, never a generic fallback. */
function summarizeSelection(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  return `${names[0]} + ${names.length - 1}`;
}
